/**
 * CreateDocumentRequest — Admin/editor creates a prestudy document request.
 *
 * Specifies which document types the facility should upload before
 * the scheduled survey visit. Facility users with login access see
 * the request and upload documents via PrestudyUpload.
 *
 * Route: /visits/:visitId/prestudy/create
 */

import { useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ClipboardList, CheckCircle } from 'lucide-react'
import {
  createDocumentRequest,
  PRESTUDY_DOC_CATEGORIES,
  type PrestudyDocType,
} from '../../lib/services/prestudy'

export function CreateLink() {
  const { visitId } = useParams<{ visitId: string }>()

  const [selectedDocs, setSelectedDocs] = useState<PrestudyDocType[]>([
    'policy', 'credential', 'certificate', 'log', 'emergency_plan',
  ])
  const [notes, setNotes] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [created, setCreated] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleDoc = useCallback((type: PrestudyDocType) => {
    setSelectedDocs((prev) =>
      prev.includes(type) ? prev.filter((d) => d !== type) : [...prev, type],
    )
  }, [])

  const handleCreate = useCallback(async () => {
    if (!visitId || selectedDocs.length === 0) return
    setIsCreating(true)
    setError(null)
    try {
      await createDocumentRequest(visitId, {
        requested_docs: selectedDocs,
        notes: notes.trim() || undefined,
      })
      setCreated(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create request')
    } finally {
      setIsCreating(false)
    }
  }, [visitId, selectedDocs, notes])

  if (!visitId) return null

  return (
    <div className="space-y-4 px-4 py-6 max-w-lg mx-auto">
      <div className="flex items-center gap-2">
        <Link to={`/visits/${visitId}/survey/documents`} className="text-slate-400 hover:text-slate-600">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="section-title">Request Pre-Survey Documents</h1>
      </div>

      {created ? (
        <div className="space-y-4">
          <div className="app-surface p-5 text-center space-y-3">
            <CheckCircle size={40} className="mx-auto text-emerald-500" />
            <h2 className="text-sm font-bold text-slate-800">Request Created</h2>
            <p className="text-xs text-slate-500">
              Facility users assigned to this visit can now see the document request
              and upload the specified documents.
            </p>
          </div>
          <Link to={`/visits/${visitId}/survey/documents`} className="btn-primary w-full block text-center text-sm py-3">
            Back to Documents
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="app-surface p-4 space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Requested Documents</span>
            <p className="text-[11px] text-slate-400">
              Select which documents the facility should upload before the visit.
              Assigned facility users will see this checklist when they log in.
            </p>
            <div className="space-y-2">
              {PRESTUDY_DOC_CATEGORIES.map((cat) => (
                <label key={cat.type} className="flex items-start gap-2.5 py-1">
                  <input
                    type="checkbox"
                    checked={selectedDocs.includes(cat.type)}
                    onChange={() => toggleDoc(cat.type)}
                    className="mt-0.5 rounded border-slate-300"
                  />
                  <div>
                    <span className="text-sm font-semibold text-slate-700">{cat.label}</span>
                    <p className="text-[10px] text-slate-400">{cat.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="app-surface p-4 space-y-2">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any specific instructions for the facility..."
              rows={3}
              className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
            />
          </div>

          {error && (
            <p className="text-xs text-red-600 text-center">{error}</p>
          )}

          <button
            type="button"
            onClick={handleCreate}
            disabled={isCreating || selectedDocs.length === 0}
            className="btn-primary w-full py-3 disabled:opacity-60"
          >
            <ClipboardList size={14} className="inline mr-2" />
            {isCreating ? 'Creating...' : 'Create Document Request'}
          </button>
        </div>
      )}
    </div>
  )
}
